import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useSEO, generateWebPageSchema } from '../../utils/seo';

const PrayerReservationPage = () => {
  const navigate = useNavigate();

  // SEO設定
  useSEO({
    title: '京都山科 御祈祷予約 | 大石神社',
    description: '大石神社の御祈祷予約ページです。家内安全、商売繁盛、学業成就、合格祈願、厄除け、安産祈願、初宮参り、七五三、交通安全など各種御祈祷を承っております。オンラインで簡単にご予約いただけます。',
    keywords: '御祈祷,祈祷予約,家内安全,商売繁盛,学業成就,合格祈願,厄除け,安産祈願,初宮参り,七五三,交通安全,大石神社,京都,山科',
    canonical: '/prayer-reservation',
    schema: generateWebPageSchema('御祈祷予約', '大石神社の御祈祷予約。家内安全、商売繁盛、学業成就など各種御祈祷を承っております', '/prayer-reservation')
  });
  
  const [formData, setFormData] = useState({
    name: '',
    kana: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    prayerType: '',
    participants: '1',
    address: '',
    fatherName: '',
    motherName: '',
    husbandName: '',
    husbandKana: '',
    childName: '',
    childAge: '',
    childGender: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // 予約受付停止フラグ
  const isReservationSuspended = true;

  const prayerTypes = [
    '家内安全',
    '商売繁盛',
    '学業成就',
    '合格祈願',
    '厄除け',
    '安産祈願',
    '初宮参り',
    '七五三',
    '交通安全',
    '病気平癒',
    'その他'
  ];

  const timeSlots = [
    '09:30',
    '10:00',
    '10:30',
    '11:00',
    '11:30',
    '12:00',
    '12:30',
    '13:00',
    '13:30',
    '14:00',
    '14:30',
    '15:00',
    '15:30'
  ];

  // カレンダー用の日付生成
  const generateCalendarDays = (targetMonth: Date) => {
    const year = targetMonth.getFullYear();
    const month = targetMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // 前月の空白
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    
    // 当月の日付
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      days.push(date);
    }
    
    return days;
  };

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDisplayDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
    const weekday = weekdays[date.getDay()];
    return `${year}年${month}月${day}日(${weekday})`;
  };

  const handleDateSelect = (date: Date) => {
    setFormData({
      ...formData,
      date: formatDate(date)
    });
    setShowCalendar(false);
  };

  const handleDateInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      date: e.target.value
    });
  };

  const isDateDisabled = (date: Date) => {
    // 当日から予約可能
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // 3ヶ月先まで予約可能
    const maxDate = new Date(today);
    maxDate.setMonth(maxDate.getMonth() + 3);
    
    return date < today || date > maxDate;
  };

  const canNavigateNext = () => {
    const today = new Date();
    const maxMonth = new Date(today);
    maxMonth.setMonth(maxMonth.getMonth() + 3);
    
    return currentMonth.getMonth() < maxMonth.getMonth() || 
           currentMonth.getFullYear() < maxMonth.getFullYear();
  };

  const canNavigatePrev = () => {
    const today = new Date();
    return currentMonth.getMonth() > today.getMonth() || 
           currentMonth.getFullYear() > today.getFullYear();
  };

  const handlePrevMonth = () => {
    if (canNavigatePrev()) {
      setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    }
  };

  const handleNextMonth = () => {
    if (canNavigateNext()) {
      setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 予約停止中は送信しない
    if (isReservationSuspended) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const { data: reservationData, error } = await supabase
        .from('prayer_reservations')
        .insert({
          user_id: null,
          name: formData.name,
          kana: formData.kana,
          email: formData.email,
          phone: formData.phone,
          prayer_date: formData.date,
          prayer_time: formData.time,
          prayer_type: formData.prayerType,
          participants: parseInt(formData.participants),
          address: formData.address || null,
          father_name: formData.fatherName || null,
          mother_name: formData.motherName || null,
          husband_name: formData.husbandName || null,
          husband_kana: formData.husbandKana || null,
          child_name: formData.childName || null,
          child_age: formData.childAge || null,
          child_gender: formData.childGender || null,
          message: formData.message || null,
          status: 'pending',
        })
        .select()
        .single();

      if (error) throw error;

      // メール送信
      const emailResponse = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-prayer-reservation-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            reservation: {
              name: formData.name,
              kana: formData.kana,
              email: formData.email,
              phone: formData.phone,
              prayer_date: formatDisplayDate(formData.date),
              prayer_time: formData.time,
              prayer_type: formData.prayerType,
              participants: formData.participants,
              address: formData.address,
              father_name: formData.fatherName,
              mother_name: formData.motherName,
              husband_name: formData.husbandName,
              husband_kana: formData.husbandKana,
              child_name: formData.childName,
              child_age: formData.childAge,
              child_gender: formData.childGender,
              message: formData.message,
            },
          }),
        }
      );

      if (!emailResponse.ok) {
        console.error('メール送信に失敗しました');
      }

      setSubmitStatus('success');
      
      // フォームをリセット
      setFormData({
        name: '',
        kana: '',
        email: '',
        phone: '',
        date: '',
        time: '',
        prayerType: '',
        participants: '1',
        address: '',
        fatherName: '',
        motherName: '',
        husbandName: '',
        husbandKana: '',
        childName: '',
        childAge: '',
        childGender: '',
        message: ''
      });

      // 5秒後にトップページへ遷移
      setTimeout(() => {
        navigate('/');
      }, 5000);
    } catch (error) {
      console.error('Error submitting reservation:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const showParentNames = ['初宮参り', '七五三'].includes(formData.prayerType);
  const showAddress = ['初宮参り', '七五三'].includes(formData.prayerType);
  const showHusbandName = formData.prayerType === '安産祈願';
  const showChildName = ['初宮参り', '七五三'].includes(formData.prayerType);
  const showChildDetails = formData.prayerType === '七五三';

  const calendarDays = generateCalendarDays(currentMonth);
  const currentMonthName = `${currentMonth.getFullYear()}年${currentMonth.getMonth() + 1}月`;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      
      <div className="pt-32 pb-24 px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-[#D4AF37] font-serif text-5xl font-bold mb-4 tracking-wide">祈祷予約</h1>
            <div className="w-24 h-[1px] bg-[#D4AF37] mx-auto mb-6"></div>
            <p className="text-white text-sm tracking-wide">
              ご祈祷のご予約を承ります。下記フォームよりお申し込みください。
            </p>
          </motion.div>

          {isReservationSuspended && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8 bg-amber-900/30 border border-amber-500 rounded-lg p-6"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                  <i className="ri-information-line text-amber-400 text-3xl"></i>
                </div>
                <div>
                  <h3 className="text-amber-400 font-bold text-lg mb-2 tracking-wide">オンライン予約受付を一時停止しております</h3>
                  <p className="text-amber-200 text-sm leading-relaxed">
                    現在、オンラインでの御祈祷予約受付を一時停止しております。<br />
                    御祈祷をご希望の方は、お手数ですがお電話（075-581-5645）にて直接お問い合わせください。<br />
                    受付時間：9:00〜17:00
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-[#1A1A1A] border border-[#D4AF37]/30 rounded-lg p-10"
          >
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-[#D4AF37] text-sm mb-2 tracking-wide">
                      お名前 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full bg-black text-white border border-[#D4AF37]/50 rounded-md px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-all"
                      placeholder="山田太郎"
                    />
                  </div>

                  <div>
                    <label className="block text-[#D4AF37] text-sm mb-2 tracking-wide">
                      フリガナ <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="kana"
                      value={formData.kana}
                      onChange={handleChange}
                      required
                      className="w-full bg-black text-white border border-[#D4AF37]/50 rounded-md px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-all"
                      placeholder="ヤマダタロウ"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-[#D4AF37] text-sm mb-2 tracking-wide">
                      メールアドレス <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full bg-black text-white border border-[#D4AF37]/50 rounded-md px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-all"
                      placeholder="example@email.com"
                    />
                  </div>

                  <div>
                    <label className="block text-[#D4AF37] text-sm mb-2 tracking-wide">
                      電話番号 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full bg-black text-white border border-[#D4AF37]/50 rounded-md px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-all"
                      placeholder="090-1234-5678"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="relative">
                    <label className="block text-[#D4AF37] text-sm mb-2 tracking-wide">
                      ご希望日 <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        name="date"
                        value={formData.date}
                        onChange={handleDateInput}
                        onFocus={() => setShowCalendar(false)}
                        required
                        min={formatDate(new Date())}
                        max={formatDate(new Date(new Date().setMonth(new Date().getMonth() + 3)))}
                        className="w-full bg-black text-white border border-[#D4AF37]/50 rounded-md px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-all cursor-pointer"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCalendar(!showCalendar)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-[#D4AF37] hover:text-white transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-calendar-line"></i>
                      </button>
                    </div>
                    
                    {showCalendar && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="absolute z-50 mt-2 bg-[#1A1A1A] border border-[#D4AF37]/50 rounded-lg p-4 shadow-2xl w-full"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <button
                            type="button"
                            onClick={handlePrevMonth}
                            disabled={!canNavigatePrev()}
                            className={`text-[#D4AF37] hover:text-white transition-colors cursor-pointer whitespace-nowrap ${!canNavigatePrev() ? 'opacity-30 cursor-not-allowed' : ''}`}
                          >
                            <i className="ri-arrow-left-s-line text-xl"></i>
                          </button>
                          <div className="text-center text-[#D4AF37] font-semibold text-sm">
                            {currentMonthName}
                          </div>
                          <button
                            type="button"
                            onClick={handleNextMonth}
                            disabled={!canNavigateNext()}
                            className={`text-[#D4AF37] hover:text-white transition-colors cursor-pointer whitespace-nowrap ${!canNavigateNext() ? 'opacity-30 cursor-not-allowed' : ''}`}
                          >
                            <i className="ri-arrow-right-s-line text-xl"></i>
                          </button>
                        </div>
                        <div className="grid grid-cols-7 gap-1 mb-2">
                          {['日', '月', '火', '水', '木', '金', '土'].map((day) => (
                            <div key={day} className="text-center text-[#D4AF37] text-xs font-semibold py-1">
                              {day}
                            </div>
                          ))}
                        </div>
                        <div className="grid grid-cols-7 gap-1">
                          {calendarDays.map((date, index) => {
                            if (!date) {
                              return <div key={`empty-${index}`} className="aspect-square"></div>;
                            }
                            
                            const disabled = isDateDisabled(date);
                            const isSelected = formData.date === formatDate(date);
                            const isToday = formatDate(date) === formatDate(new Date());
                            
                            return (
                              <button
                                key={index}
                                type="button"
                                onClick={() => !disabled && handleDateSelect(date)}
                                disabled={disabled}
                                className={`aspect-square flex items-center justify-center text-xs rounded-md transition-all cursor-pointer whitespace-nowrap
                                  ${disabled ? 'text-gray-600 cursor-not-allowed' : 'text-white hover:bg-[#D4AF37]/20'}
                                  ${isSelected ? 'bg-[#D4AF37] text-black font-bold' : ''}
                                  ${isToday && !isSelected ? 'border border-[#D4AF37]' : ''}
                                `}
                              >
                                {date.getDate()}
                              </button>
                            );
                          })}
                        </div>
                        <div className="mt-3 pt-3 border-t border-[#D4AF37]/20">
                          <button
                            type="button"
                            onClick={() => setShowCalendar(false)}
                            className="w-full text-[#D4AF37] text-xs hover:text-white transition-colors cursor-pointer whitespace-nowrap"
                          >
                            閉じる
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </div>

                  <div>
                    <label className="block text-[#D4AF37] text-sm mb-2 tracking-wide">
                      ご希望時間 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="time"
                      value={formData.time}
                      onChange={handleChange}
                      required
                      className="w-full bg-black text-white border border-[#D4AF37]/50 rounded-md px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-all cursor-pointer"
                    >
                      <option value="">選択してください</option>
                      {timeSlots.map((time) => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-[#D4AF37] text-sm mb-2 tracking-wide">
                    祈祷種類 <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="prayerType"
                    value={formData.prayerType}
                    onChange={handleChange}
                    required
                    className="w-full bg-black text-white border border-[#D4AF37]/50 rounded-md px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-all cursor-pointer"
                  >
                    <option value="">選択してください</option>
                    {prayerTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                {showChildName && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <label className="block text-[#D4AF37] text-sm mb-2 tracking-wide">
                      お祝いのお子様のお名前 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="childName"
                      value={formData.childName}
                      onChange={handleChange}
                      required
                      className="w-full bg-black text-white border border-[#D4AF37]/50 rounded-md px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-all"
                      placeholder="山田一郎"
                    />
                  </motion.div>
                )}

                {showChildDetails && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="grid grid-cols-2 gap-6"
                  >
                    <div>
                      <label className="block text-[#D4AF37] text-sm mb-2 tracking-wide">
                        お子様の年齢 <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="childAge"
                        value={formData.childAge}
                        onChange={handleChange}
                        required
                        className="w-full bg-black text-white border border-[#D4AF37]/50 rounded-md px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-all cursor-pointer"
                      >
                        <option value="">選択してください</option>
                        <option value="3歳">3歳</option>
                        <option value="5歳">5歳</option>
                        <option value="7歳">7歳</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-[#D4AF37] text-sm mb-2 tracking-wide">
                        お子様の性別 <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="childGender"
                        value={formData.childGender}
                        onChange={handleChange}
                        required
                        className="w-full bg-black text-white border border-[#D4AF37]/50 rounded-md px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-all cursor-pointer"
                      >
                        <option value="">選択してください</option>
                        <option value="男の子">男の子</option>
                        <option value="女の子">女の子</option>
                      </select>
                    </div>
                  </motion.div>
                )}

                {showAddress && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <label className="block text-[#D4AF37] text-sm mb-2 tracking-wide">
                      ご住所 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      className="w-full bg-black text-white border border-[#D4AF37]/50 rounded-md px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-all"
                      placeholder="京都府京都市山科区西野山桜ノ馬場町116"
                    />
                  </motion.div>
                )}

                {showParentNames && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="grid grid-cols-2 gap-6"
                  >
                    <div>
                      <label className="block text-[#D4AF37] text-sm mb-2 tracking-wide">
                        お子様の父親の名前
                      </label>
                      <input
                        type="text"
                        name="fatherName"
                        value={formData.fatherName}
                        onChange={handleChange}
                        className="w-full bg-black text-white border border-[#D4AF37]/50 rounded-md px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-all"
                        placeholder="山田太郎"
                      />
                    </div>

                    <div>
                      <label className="block text-[#D4AF37] text-sm mb-2 tracking-wide">
                        お子様の母親の名前
                      </label>
                      <input
                        type="text"
                        name="motherName"
                        value={formData.motherName}
                        onChange={handleChange}
                        className="w-full bg-black text-white border border-[#D4AF37]/50 rounded-md px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-all"
                        placeholder="山田花子"
                      />
                    </div>
                  </motion.div>
                )}

                {showHusbandName && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="grid grid-cols-2 gap-6"
                  >
                    <div>
                      <label className="block text-[#D4AF37] text-sm mb-2 tracking-wide">
                        夫の名前 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="husbandName"
                        value={formData.husbandName}
                        onChange={handleChange}
                        required
                        className="w-full bg-black text-white border border-[#D4AF37]/50 rounded-md px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-all"
                        placeholder="山田太郎"
                      />
                    </div>

                    <div>
                      <label className="block text-[#D4AF37] text-sm mb-2 tracking-wide">
                        夫のフリガナ <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="husbandKana"
                        value={formData.husbandKana}
                        onChange={handleChange}
                        required
                        className="w-full bg-black text-white border border-[#D4AF37]/50 rounded-md px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-all"
                        placeholder="ヤマダタロウ"
                      />
                    </div>
                  </motion.div>
                )}

                <div>
                  <label className="block text-[#D4AF37] text-sm mb-2 tracking-wide">
                    参加人数
                  </label>
                  <input
                    type="number"
                    name="participants"
                    value={formData.participants}
                    onChange={handleChange}
                    min="1"
                    max="20"
                    className="w-full bg-black text-white border border-[#D4AF37]/50 rounded-md px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-all"
                  />
                </div>

                <div>
                  <label className="block text-[#D4AF37] text-sm mb-2 tracking-wide">
                    ご要望・備考
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    maxLength={500}
                    rows={4}
                    className="w-full bg-black text-white border border-[#D4AF37]/50 rounded-md px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-all resize-none"
                    placeholder="特別なご要望がございましたらご記入ください（500文字以内）"
                  />
                  <p className="text-gray-500 text-xs mt-1">{formData.message.length}/500文字</p>
                </div>

                {submitStatus === 'success' && (
                  <div className="bg-green-900/30 border border-green-500 text-green-300 px-4 py-3 rounded-md text-sm">
                    ご予約を承りました。ご入力いただいたメールアドレス宛に確認メールをお送りいたします。
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-900/30 border border-red-500 text-red-300 px-4 py-3 rounded-md text-sm">
                    送信に失敗しました。お手数ですが、もう一度お試しください。
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting || isReservationSuspended}
                  className="w-full bg-[#D4AF37] text-black font-semibold py-4 rounded-md hover:bg-[#C4A037] transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm tracking-wide cursor-pointer whitespace-nowrap"
                >
                  {isReservationSuspended ? '予約受付停止中' : (isSubmitting ? '送信中...' : '予約を申し込む')}
                </button>
              </div>
            </form>

            <div className="mt-8 pt-8 border-t border-[#D4AF37]/20">
              <p className="text-gray-400 text-xs leading-relaxed">
                ※ご予約は当日でも承っております。<br />
                ※初穂料は祈祷の種類により異なります。詳しくはお電話にてお問い合わせください。<br />
                ※お急ぎの場合はお電話（075-581-5645）にて承ります。
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrayerReservationPage;